import React, { useState, useEffect, useMemo } from "react";
import { Form, Spinner } from "react-bootstrap";
import { listCustomScriptsForObject } from "../../api/CustomScriptApi";

/**
 * Config: { customScriptId, scriptKey?: string, displayLabel?: string }
 * Backend action "Run Custom Script" runs the VM asynchronously (setImmediate).
 */
export default function WorkflowRunCustomScript({
  config,
  onChange,
  onConfigPatch,
  objectName,
  readOnly,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!objectName) {
        setRows([]);
        return;
      }
      setLoading(true);
      setLoadError("");
      try {
        const res = await listCustomScriptsForObject(objectName, {
          matchObjectOnly: false,
        });
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          const allow = new Set(["backend", "workflow_script"]);
          const filtered = res.data.filter((r) =>
            allow.has(String(r.script_type || "").toLowerCase())
          );
          setRows(filtered);
        } else {
          setRows([]);
          setLoadError(res?.message || "Could not load custom scripts.");
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setLoadError(e?.message || "Failed to load custom scripts.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [objectName]);

  const byId = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      if (r.id) m.set(String(r.id), r);
    }
    return m;
  }, [rows]);

  const value = config?.customScriptId != null ? String(config.customScriptId) : "";

  return (
    <Form.Group className="mb-3">
      <Form.Label>Custom script (backend or workflow)</Form.Label>
      {loading && (
        <div className="text-muted small mb-2">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading scripts…
        </div>
      )}
      {loadError && !loading && (
        <div className="text-danger small mb-2">{loadError}</div>
      )}
      <Form.Select
        value={value}
        onChange={(e) => {
          const id = e.target.value;
          if (!id) {
            if (onConfigPatch) {
              onConfigPatch({ customScriptId: "", scriptKey: "", displayLabel: "" });
            } else {
              onChange("customScriptId", "");
              onChange("scriptKey", "");
              onChange("displayLabel", "");
            }
            return;
          }
          const row = byId.get(id);
          const patch = {
            customScriptId: id,
            scriptKey: row?.script_key != null ? String(row.script_key) : "",
            displayLabel:
              row?.display_label != null
                ? String(row.display_label)
                : row?.script_key != null
                  ? String(row.script_key)
                  : "",
          };
          if (onConfigPatch) onConfigPatch(patch);
          else {
            onChange("customScriptId", patch.customScriptId);
            onChange("scriptKey", patch.scriptKey);
            onChange("displayLabel", patch.displayLabel);
          }
        }}
        disabled={readOnly || !objectName || loading}
      >
        <option value="">
          {!objectName
            ? "Select a workflow object first"
            : "— Select a backend script —"}
        </option>
        {rows.map((r) => (
          <option key={r.id} value={r.id}>
            {r.display_label || r.script_key || r.id}
            {r.script_key && r.display_label
              ? ` (${r.script_key})`
              : ""}
          </option>
        ))}
      </Form.Select>
      <Form.Text className="text-muted">
        Lists <code>script_type = backend</code> or <code>workflow_script</code> (object +
        universal). Use a single entry <code>async function main(params)</code>:{" "}
        <code>params.workflow</code> has <code>eventType</code>, <code>oldValues</code>,{" "}
        <code>newValues</code> ( <code>{"{ fieldName, value }[]"}</code> ), and{" "}
        <code>fieldChanges</code>; <code>params.run.from</code> is <code>workflow</code>. Legacy
        scripts may still use the same fields as globals. <code>workflow_script</code> cannot run
        from POST /api/custom-scripts/run. Execution is queued in the background like other workflow
        actions.
      </Form.Text>
    </Form.Group>
  );
}
